/**
 * SuggestedQuestionsManager
 * CRUD interface for managing suggested questions for document chat
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSuggestedQuestions,
  addSuggestedQuestion,
  updateSuggestedQuestion,
  deleteSuggestedQuestion,
  reorderSuggestedQuestions,
} from "../../services/chat.service";
import { useToast } from "../ui/Toast";
import Button from "../common/Button";
import type { UUID } from "../../types/common.types";
import type { SuggestedQuestion } from "../../types/chat.types";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

interface SuggestedQuestionsManagerProps {
  contentItemId: UUID;
}

export default function SuggestedQuestionsManager({
  contentItemId,
}: SuggestedQuestionsManagerProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Local state
  const [isAdding, setIsAdding] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [editingId, setEditingId] = useState<UUID | null>(null);
  const [editingText, setEditingText] = useState("");
  const [draggedId, setDraggedId] = useState<UUID | null>(null);

  // Fetch questions
  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["suggestedQuestions", contentItemId],
    queryFn: () => getSuggestedQuestions(contentItemId),
  });

  // Add mutation
  const addMutation = useMutation({
    mutationFn: async (question: string) => {
      const displayOrder = questions.length;
      return addSuggestedQuestion(contentItemId, question, displayOrder);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["suggestedQuestions", contentItemId],
      });
      setNewQuestion("");
      setIsAdding(false);
      showToast("success", "Question added");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to add question: ${error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, question }: { id: UUID; question: string }) => {
      return updateSuggestedQuestion(id, { question });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["suggestedQuestions", contentItemId],
      });
      setEditingId(null);
      showToast("success", "Question updated");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to update question: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: UUID) => {
      return deleteSuggestedQuestion(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["suggestedQuestions", contentItemId],
      });
      showToast("success", "Question deleted");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to delete question: ${error.message}`);
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: async (questionIds: UUID[]) => {
      return reorderSuggestedQuestions(contentItemId, questionIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["suggestedQuestions", contentItemId],
      });
    },
    onError: (error: Error) => {
      showToast("error", `Failed to reorder questions: ${error.message}`);
    },
  });

  const handleAdd = () => {
    const trimmed = newQuestion.trim();
    if (trimmed) {
      addMutation.mutate(trimmed);
    }
  };

  const handleEdit = (question: SuggestedQuestion) => {
    setEditingId(question.id);
    setEditingText(question.question);
  };

  const handleSaveEdit = () => {
    if (editingId && editingText.trim()) {
      updateMutation.mutate({ id: editingId, question: editingText.trim() });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const handleDelete = (id: UUID) => {
    if (window.confirm("Delete this suggested question?")) {
      deleteMutation.mutate(id);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, id: UUID) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetId: UUID) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }

    // Reorder the questions
    const currentOrder = questions.map((q) => q.id);
    const draggedIndex = currentOrder.indexOf(draggedId);
    const targetIndex = currentOrder.indexOf(targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedId(null);
      return;
    }

    // Remove dragged item and insert at target position
    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedId);

    reorderMutation.mutate(newOrder);
    setDraggedId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading questions...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Suggested Questions
        </label>
        {!isAdding && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" />
            Add
          </button>
        )}
      </div>

      {/* Add new question form */}
      {isAdding && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              } else if (e.key === "Escape") {
                setIsAdding(false);
                setNewQuestion("");
              }
            }}
            placeholder="What would you like to know?"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          <Button
            size="sm"
            onClick={handleAdd}
            isLoading={addMutation.isPending}
            disabled={!newQuestion.trim()}
          >
            <CheckIcon className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setIsAdding(false);
              setNewQuestion("");
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Questions list */}
      {questions.length === 0 ? (
        <p className="text-sm text-gray-500">
          No suggested questions yet. Add some to help users get started.
        </p>
      ) : (
        <ul className="space-y-2">
          {questions.map((question) => (
            <li
              key={question.id}
              draggable={editingId !== question.id}
              onDragStart={(e) => handleDragStart(e, question.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, question.id)}
              onDragEnd={handleDragEnd}
              className={clsx(
                "flex items-center gap-2 p-2 bg-gray-50 rounded-lg group",
                draggedId === question.id && "opacity-50",
                editingId !== question.id && "cursor-move",
              )}
            >
              {/* Drag handle */}
              <Bars3Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />

              {/* Question text or edit input */}
              {editingId === question.id ? (
                <div className="flex-1 flex gap-2">
                  <input
                    type="text"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSaveEdit();
                      } else if (e.key === "Escape") {
                        handleCancelEdit();
                      }
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                    className="p-1 text-green-600 hover:text-green-700"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="p-1 text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <span className="flex-1 text-sm text-gray-700 truncate">
                    {question.question}
                  </span>

                  {/* Action buttons (visible on hover) */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => handleEdit(question)}
                      className="p-1 text-gray-500 hover:text-blue-600"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(question.id)}
                      disabled={deleteMutation.isPending}
                      className="p-1 text-gray-500 hover:text-red-600"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-500">
        Drag to reorder. These questions appear when users first open the chat.
      </p>
    </div>
  );
}
